import React from 'react';
import { Box, Typography, TextField, FormControl, RadioGroup, FormControlLabel, Radio, Button } from '@mui/material';
import api from '../../../services/api';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';

export interface VideoContent {
    url: string;
    alt?: string;
    description?: string;
    sourceType: 'url' | 'upload' | 'library';
    player: 'youtube' | 'native';
}

interface VideoSettingsProps {
    content: VideoContent;
    onUpdate: (content: VideoContent) => void;
    setMediaLibraryOpen: (open: boolean) => void;
}

const VideoSettings: React.FC<VideoSettingsProps> = ({ content, onUpdate, setMediaLibraryOpen }) => {

    const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const formData = new FormData();
            formData.append('file', e.target.files[0]);
            try {
                const res = await api.post('/api/media/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                onUpdate({ ...content, url: res.data.url, sourceType: 'upload', player: 'native' });
            } catch (err) {
                console.error("Upload failed", err);
            }
        }
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', mb: 2, color: '#4a5568' }}>Configuration de la vidéo</Typography>

            <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', p: 2, borderRadius: 3, mb: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', mb: 1, display: 'block' }}>SOURCE DE LA VIDÉO</Typography>
                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        value={content.sourceType || 'url'}
                        onChange={(e) => onUpdate({ ...content, sourceType: e.target.value as any })}
                    >
                        <FormControlLabel value="url" control={<Radio size="small" sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }} />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>URL Externe</Typography>} />
                        <FormControlLabel value="upload" control={<Radio size="small" sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }} />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Télécharger</Typography>} />
                        <FormControlLabel value="library" control={<Radio size="small" sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }} />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Médiathèque</Typography>} />
                    </RadioGroup>
                </FormControl>
            </Box>

            {content.sourceType === 'url' && (
                <Box sx={{ mb: 3 }}>
                    <TextField
                        fullWidth
                        label="URL de la vidéo (YouTube, Vimeo...)"
                        value={content.url || ''}
                        onChange={(e) => onUpdate({ ...content, url: e.target.value })}
                        size="small"
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)', p: 2, borderRadius: 3 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#718096', mb: 1, display: 'block' }}>TYPE DE LECTEUR</Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                row
                                value={content.player || 'youtube'}
                                onChange={(e) => onUpdate({ ...content, player: e.target.value as any })}
                            >
                                <FormControlLabel value="youtube" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Intégré (YouTube/Vimeo)</Typography>} />
                                <FormControlLabel value="native" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Natif (HTML5)</Typography>} />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </Box>
            )}

            {content.sourceType === 'upload' && (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#9c27b0',
                            color: '#9c27b0',
                            '&:hover': { borderColor: '#7b1fa2', bgcolor: 'rgba(156, 39, 176, 0.05)' }
                        }}
                    >
                        Choisir un fichier vidéo
                        <input type="file" hidden accept="video/*" onChange={handleVideoUpload} />
                    </Button>
                    {content.url && <Typography variant="caption" display="block" sx={{ mt: 1, color: '#10b981', fontWeight: 600 }}>✓ Fichier prêt: {content.url.split('/').pop()}</Typography>}
                </Box>
            )}

            {content.sourceType === 'library' && (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setMediaLibraryOpen(true)}
                        fullWidth
                        startIcon={<LibraryBooksIcon />}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#9c27b0',
                            color: '#9c27b0',
                            '&:hover': { borderColor: '#7b1fa2', bgcolor: 'rgba(156, 39, 176, 0.05)' }
                        }}
                    >
                        Ouvrir la médiathèque
                    </Button>
                    {content.url && <Typography variant="caption" display="block" sx={{ mt: 1, color: '#10b981', fontWeight: 600 }}>✓ Vidéo sélectionnée: {content.url.split('/').pop()}</Typography>}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                    fullWidth
                    label="Texte alternatif"
                    value={content.alt || ''}
                    onChange={(e) => onUpdate({ ...content, alt: e.target.value })}
                    size="small"
                    placeholder="Ex: Présentation de la leçon"
                />
                <TextField
                    fullWidth
                    label="Légende"
                    value={content.description || ''}
                    onChange={(e) => onUpdate({ ...content, description: e.target.value })}
                    size="small"
                    placeholder="Texte affiché sous la vidéo"
                />
            </Box>

            {content.url && (
                <Box sx={{ mt: 2, borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)' }}>
                    {content.player === 'youtube' ? (
                        <iframe
                            width="100%"
                            height="380"
                            src={content.url.replace('watch?v=', 'embed/')}
                            title={content.alt || "Video player"}
                            frameBorder="0"
                            allowFullScreen
                        ></iframe>
                    ) : (
                        <video width="100%" controls src={content.url} style={{ display: 'block' }}>
                            {content.alt}
                        </video>
                    )}
                    {content.description && (
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', textAlign: 'center', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                            <Typography variant="caption" sx={{ color: '#718096', fontStyle: 'italic', fontWeight: 600 }}>{content.description}</Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default VideoSettings;
